import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ZoomToolbar,
  buildViewerTransform,
  formatTeacherUploadTime,
  normalizeViewerScale,
  CountDistributionItem,
  ImageRecord,
  STAR_LABELS,
  TeacherAnalyticsRange,
  TeacherLoadingState,
  TeacherPageShell,
  getAssetUrl,
  useTeacherAdventureAnalytics,
} from './teacherAdventureShared';

const RANGE_OPTIONS: Array<{ value: TeacherAnalyticsRange; label: string }> = [
  { value: 'today', label: '今天' },
  { value: 'yesterday', label: '昨天' },
  { value: 'week', label: '本周' },
  { value: 'all', label: '全部' },
];

const ESCAPE_PAGE_SIZE = 12;

const DistributionList: React.FC<{
  title: string;
  items: CountDistributionItem[];
  emptyText: string;
  tone: 'amber' | 'sky' | 'emerald';
}> = ({ title, items, emptyText, tone }) => {
  const maxCount = Math.max(...items.map((item) => item.count), 1);
  const toneClasses = {
    amber: {
      card: 'border-amber-200 bg-amber-50/90',
      badge: 'text-amber-700',
      track: 'bg-amber-100',
      bar: 'bg-amber-500',
    },
    sky: {
      card: 'border-sky-200 bg-sky-50/90',
      badge: 'text-sky-700',
      track: 'bg-sky-100',
      bar: 'bg-sky-500',
    },
    emerald: {
      card: 'border-emerald-200 bg-emerald-50/90',
      badge: 'text-emerald-700',
      track: 'bg-emerald-100',
      bar: 'bg-emerald-500',
    },
  } as const;
  const toneStyle = toneClasses[tone];

  return (
    <article
      data-testid={`distribution-card-${title}`}
      className={`rounded-[1.75rem] border p-5 shadow-sm ${toneStyle.card}`}
    >
      <div className={`text-xs font-semibold tracking-[0.24em] ${toneStyle.badge}`}>课堂分布</div>
      <h2 className="mt-2 text-xl font-black text-slate-950">{title}</h2>
      <div className="mt-5 space-y-4">
        {items.length > 0 ? items.map((item) => (
          <div key={`${title}-${item.name}`} className="space-y-2">
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="font-semibold text-slate-800">{item.name}</span>
              <span className="font-semibold text-slate-950">{item.count}</span>
            </div>
            <div className={`h-3 overflow-hidden rounded-full ${toneStyle.track}`}>
              <div
                className={`h-full rounded-full ${toneStyle.bar}`}
                style={{ width: `${Math.max((item.count / maxCount) * 100, 8)}%` }}
              />
            </div>
          </div>
        )) : <div className="text-sm text-slate-500">{emptyText}</div>}
      </div>
    </article>
  );
};

const WorkCard: React.FC<{
  record: ImageRecord;
  imageLabel: string;
  onOpenDetail: (record: ImageRecord) => void;
  onOpenPreview: (url: string) => void;
}> = ({ record, imageLabel, onOpenDetail, onOpenPreview }) => (
  <article className="overflow-hidden rounded-[1.5rem] border border-stone-200 bg-stone-50 shadow-sm">
    <button
      type="button"
      onClick={() => onOpenPreview(getAssetUrl(record.imageUrl))}
      className="block w-full bg-stone-200"
      aria-label={`预览${record.studentName}的${imageLabel}缩略图`}
    >
      <img
        src={getAssetUrl(record.thumbnailUrl || record.imageUrl)}
        alt={`${record.studentName} 的${imageLabel}缩略图`}
        className="h-52 w-full object-cover"
      />
    </button>
    <div className="flex items-center justify-between gap-3 px-4 py-4">
      <div className="min-w-0">
        <div className="truncate text-base font-bold text-slate-950">{record.studentName}</div>
        <div className="mt-1 text-xs text-slate-500">{formatTeacherUploadTime(record.createdAt)}</div>
      </div>
      <button
        type="button"
        onClick={() => onOpenDetail(record)}
        aria-label={`查看${record.studentName}的${imageLabel}详情`}
        className="rounded-2xl border border-stone-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800"
      >
        查看详情
      </button>
    </div>
  </article>
);

const DetailModal: React.FC<{
  record: ImageRecord | null;
  title: string;
  imageLabel: string;
  onClose: () => void;
  onOpenPreview: (url: string) => void;
}> = ({ record, title, imageLabel, onClose, onOpenPreview }) => {
  if (!record) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/45 px-4 py-6">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-[2rem] bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-2xl font-black text-slate-950">{record.studentName}</h3>
            <p className="mt-1 text-sm text-slate-500">{title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl border border-stone-300 px-3 py-2 text-sm font-semibold text-slate-700"
          >
            关闭
          </button>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1.3fr)_minmax(280px,0.8fr)]">
          <div className="overflow-hidden rounded-[1.75rem] bg-stone-100">
            <button
              type="button"
              onClick={() => onOpenPreview(getAssetUrl(record.imageUrl))}
              aria-label={`查看${record.studentName}的${imageLabel}大图`}
              className="block w-full"
            >
              <img
                src={getAssetUrl(record.imageUrl)}
                alt={`${record.studentName} 的${imageLabel}大图`}
                className="h-full max-h-[70vh] w-full object-contain"
              />
            </button>
          </div>

          <div className="space-y-4">
            <div className="rounded-[1.5rem] border border-stone-200 bg-stone-50 p-5">
              <div className="text-sm font-semibold text-slate-500">上传时间</div>
              <div className="mt-2 text-base font-semibold text-slate-950">{formatTeacherUploadTime(record.createdAt)}</div>
            </div>
            <div className="rounded-[1.5rem] border border-stone-200 bg-stone-50 p-5">
              <div className="text-sm font-semibold text-slate-500">学生</div>
              <div className="mt-2 text-lg font-bold text-slate-950">{record.studentName}</div>
            </div>

            {record.feedback?.encouragement ? (
              <div className="rounded-[1.5rem] border border-stone-200 bg-stone-50 p-5">
                <div className="text-sm font-semibold text-slate-500">点评</div>
                <p className="mt-3 text-sm leading-7 text-slate-800">{record.feedback.encouragement}</p>
              </div>
            ) : null}

            {record.feedback?.starRatings ? (
              <div className="rounded-[1.5rem] border border-stone-200 bg-stone-50 p-5">
                <div className="text-sm font-semibold text-slate-500">星级结果</div>
                <div className="mt-3 space-y-3">
                  {Object.entries(record.feedback.starRatings).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between gap-4">
                      <span className="text-sm text-slate-700">{STAR_LABELS[key] ?? key}</span>
                      <span className="text-slate-950">{'★'.repeat(Math.max(0, Math.round(value)))}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {record.feedback?.medalTitle ? (
              <div className="rounded-[1.5rem] border border-stone-200 bg-stone-50 p-5">
                <div className="text-sm font-semibold text-slate-500">专属称号</div>
                <div className="mt-2 text-lg font-bold text-slate-950">{record.feedback.medalTitle}</div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export const TeacherAnalyticsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const rangeParam = searchParams.get('range');
  const dateParam = searchParams.get('date');
  const lessonRunIdParam = searchParams.get('lessonRunId');
  const range = (RANGE_OPTIONS.find((option) => option.value === rangeParam)?.value ?? 'today') as TeacherAnalyticsRange;
  const { analytics, isLoading, loadError, reloadAnalytics } = useTeacherAdventureAnalytics({
    range: dateParam || lessonRunIdParam ? undefined : (rangeParam ? range : undefined),
    date: dateParam || undefined,
    lessonRunId: lessonRunIdParam || undefined,
  });
  const [selectedRecord, setSelectedRecord] = useState<ImageRecord | null>(null);
  const [selectedImageLabel, setSelectedImageLabel] = useState<'险情设计作品' | '逃生过程作品'>('险情设计作品');
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [previewImageScale, setPreviewImageScale] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [escapePage, setEscapePage] = useState(1);

  const selectedDate = dateParam ?? '';
  const selectedLessonRunId = lessonRunIdParam ?? '';
  const fallbackLabel = selectedDate
    ? selectedDate
    : rangeParam
      ? (RANGE_OPTIONS.find((option) => option.value === range)?.label ?? '当前课次')
      : '当前课次';
  const selectedLabel = analytics?.filter.label ?? fallbackLabel;

  const updateFilters = (next: { range?: TeacherAnalyticsRange; date?: string; lessonRunId?: string }) => {
    const nextParams = new URLSearchParams(searchParams);

    if (next.date !== undefined) {
      if (next.date) {
        nextParams.set('date', next.date);
        nextParams.delete('range');
        nextParams.delete('lessonRunId');
      } else {
        nextParams.delete('date');
      }
    }

    if (next.lessonRunId !== undefined) {
      if (next.lessonRunId) {
        nextParams.set('lessonRunId', next.lessonRunId);
      } else {
        nextParams.delete('lessonRunId');
      }
    }

    if (next.range !== undefined) {
      nextParams.set('range', next.range);
      nextParams.delete('date');
      nextParams.delete('lessonRunId');
    }

    setSearchParams(nextParams);
  };

  useEffect(() => {
    setEscapePage(1);
  }, [analytics?.classroomBoard.escapeWorks.length, selectedDate, selectedLessonRunId, range]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await reloadAnalytics({ silent: true });
    } finally {
      setIsRefreshing(false);
    }
  };

  const pagedEscapeWorks = useMemo(() => {
    const works = analytics?.classroomBoard.escapeWorks ?? [];
    const startIndex = (escapePage - 1) * ESCAPE_PAGE_SIZE;
    return works.slice(startIndex, startIndex + ESCAPE_PAGE_SIZE);
  }, [analytics?.classroomBoard.escapeWorks, escapePage]);

  const totalEscapePages = Math.max(
    1,
    Math.ceil((analytics?.classroomBoard.escapeWorks.length ?? 0) / ESCAPE_PAGE_SIZE)
  );

  if (isLoading || loadError || !analytics) {
    return (
      <TeacherPageShell title="统计分析" description="这里只保留课堂看板和作品查看。">
        <TeacherLoadingState error={loadError} loadingText="正在加载统计分析..." />
      </TeacherPageShell>
    );
  }

  return (
    <TeacherPageShell title="统计分析" description="这里只保留课堂看板和作品查看。">
      <section className="rounded-[2rem] border border-stone-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-700">当前口径：{selectedLabel}</div>
            <p className="mt-1 text-sm text-slate-500">支持按时间范围查看，也支持在指定日期下切换具体课次。</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {RANGE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-label={option.label}
                onClick={() => updateFilters({ range: option.value })}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                  !selectedDate && !selectedLessonRunId && range === option.value
                    ? 'bg-slate-950 text-white'
                    : 'border border-stone-300 bg-white text-slate-800'
                }`}
              >
                {option.label}
              </button>
            ))}
            <label className="flex items-center gap-2 rounded-full border border-stone-300 bg-white px-3 py-2 text-sm text-slate-600">
              <span>指定日期</span>
              <input
                type="date"
                aria-label="指定日期"
                value={selectedDate}
                onChange={(event) => updateFilters({ date: event.target.value })}
                className="rounded-xl border border-stone-200 px-2 py-1 text-sm text-slate-800 outline-none"
              />
            </label>
            {analytics.filter.availableLessonRuns.length > 0 ? (
              <label className="flex items-center gap-2 rounded-full border border-stone-300 bg-white px-3 py-2 text-sm text-slate-600">
                <span>课次</span>
                <select
                  aria-label="课次"
                  value={selectedLessonRunId || analytics.filter.lessonRunId || ''}
                  onChange={(event) => updateFilters({ lessonRunId: event.target.value })}
                  className="rounded-xl border border-stone-200 px-2 py-1 text-sm text-slate-800 outline-none"
                >
                  {analytics.filter.availableLessonRuns.map((lessonRun) => (
                    <option key={lessonRun.lessonRunId} value={lessonRun.lessonRunId}>
                      {lessonRun.lessonLabel}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <button
              type="button"
              onClick={() => {
                void handleRefresh();
              }}
              disabled={isRefreshing}
              className="rounded-full border border-stone-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 transition hover:border-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isRefreshing ? '刷新中...' : '刷新作品'}
            </button>
          </div>
        </div>
      </section>

      <section data-testid="distribution-grid" className="grid gap-4 md:grid-cols-3">
        <DistributionList title="队友选择分布" items={analytics.classroomBoard.teammateDistribution} emptyText="当前暂无队友选择数据。" tone="amber" />
        <DistributionList title="场景选择分布" items={analytics.classroomBoard.sceneDistribution} emptyText="当前暂无场景选择数据。" tone="sky" />
        <DistributionList title="装备选择分布" items={analytics.classroomBoard.equipmentDistribution} emptyText="当前暂无装备选择数据。" tone="emerald" />
      </section>

      <section className="rounded-[1.75rem] border border-stone-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-black text-slate-950">险情设计作品区</h2>
            <p className="mt-1 text-sm text-slate-500">按 6 个场景分组查看，点击图片看大图，点击按钮看点评。</p>
          </div>
        </div>
        <div className="mt-5 space-y-6">
          {analytics.classroomBoard.dangerWorksByScene.map((group) => (
            <section key={group.sceneGroup} className="rounded-[1.5rem] border border-stone-200 bg-stone-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-lg font-black text-slate-950">{group.sceneLabel}</h3>
                <div className="rounded-full bg-white px-3 py-1 text-sm font-semibold text-slate-800">{group.items.length} 份</div>
              </div>
              {group.items.length > 0 ? (
                <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {group.items.map((record) => (
                    <WorkCard
                      key={record.id}
                      record={record}
                      imageLabel="险情设计作品"
                      onOpenDetail={(nextRecord) => {
                        setSelectedImageLabel('险情设计作品');
                        setSelectedRecord(nextRecord);
                      }}
                      onOpenPreview={(url) => {
                        setPreviewImageUrl(url);
                        setPreviewImageScale(1);
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div className="mt-4 text-sm text-slate-500">这个场景下暂时没有作品。</div>
              )}
            </section>
          ))}
        </div>
      </section>

      <section className="rounded-[1.75rem] border border-stone-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-black text-slate-950">逃生过程作品区</h2>
            <p className="mt-1 text-sm text-slate-500">直接显示全班作品，超过 12 份自动分页。</p>
          </div>
          <div className="rounded-full bg-stone-100 px-3 py-1 text-sm font-semibold text-slate-800">
            第 {escapePage} / {totalEscapePages} 页
          </div>
        </div>

        {pagedEscapeWorks.length > 0 ? (
          <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {pagedEscapeWorks.map((record) => (
              <WorkCard
                key={record.id}
                record={record}
                imageLabel="逃生过程作品"
                onOpenDetail={(nextRecord) => {
                  setSelectedImageLabel('逃生过程作品');
                  setSelectedRecord(nextRecord);
                }}
                onOpenPreview={(url) => {
                  setPreviewImageUrl(url);
                  setPreviewImageScale(1);
                }}
              />
            ))}
          </div>
        ) : (
          <div className="mt-4 text-sm text-slate-500">当前暂无逃生过程作品。</div>
        )}

        {totalEscapePages > 1 ? (
          <div className="mt-5 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => setEscapePage((current) => Math.max(1, current - 1))}
              disabled={escapePage === 1}
              className="rounded-2xl border border-stone-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
            >
              上一页
            </button>
            <button
              type="button"
              onClick={() => setEscapePage((current) => Math.min(totalEscapePages, current + 1))}
              disabled={escapePage === totalEscapePages}
              className="rounded-2xl border border-stone-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
            >
              下一页
            </button>
          </div>
        ) : null}
      </section>

      <DetailModal
        record={selectedRecord}
        title={selectedImageLabel}
        imageLabel={selectedImageLabel}
        onClose={() => setSelectedRecord(null)}
        onOpenPreview={(url) => {
          setPreviewImageUrl(url);
          setPreviewImageScale(1);
        }}
      />

      {previewImageUrl ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 px-4 py-6" role="dialog" aria-label="图片预览">
          <div className="relative w-full max-w-5xl">
            <ZoomToolbar
              className="absolute left-0 top-0 z-20"
              onZoomOut={() => setPreviewImageScale((prev) => normalizeViewerScale(prev - 0.25))}
              onZoomIn={() =>
                setPreviewImageScale((prev) => normalizeViewerScale(prev >= 1.25 ? prev + 0.25 : 1.25))
              }
              onReset={() => setPreviewImageScale(1)}
            />
            <button
              type="button"
              onClick={() => {
                setPreviewImageUrl('');
                setPreviewImageScale(1);
              }}
              className="absolute right-0 top-0 rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow"
            >
              关闭
            </button>
            <img
              src={previewImageUrl}
              alt="预览大图"
              className="mx-auto max-h-[85vh] rounded-[1.75rem] shadow-2xl transition-transform"
              style={{ transform: buildViewerTransform(0, previewImageScale) }}
            />
          </div>
        </div>
      ) : null}
    </TeacherPageShell>
  );
};
